/**
 * core/schema/migrations.ts · 数据库 migration 注册表
 *
 * 复用 v18.9.197 schema-migrations/ 8 个 migration (已生产稳定):
 *   001: material is_active (材料启用列)
 *   002: handoff 3 表 + 6 索引 + 8 SEED 默认规则
 *   003: customer_invoices VAT (增值税)
 *   004: audit_events 5 列 + CREATE TABLE
 *   005: raw_inventory 2 表 + 5 索引 (原料库存)
 *   006: guard + returns + portal 3 表
 *   007: supplier_scores 11 列累计债清
 *   008: misc 9 列 + suppliers.deleted_at
 *
 * v300 沿用相同设计 · ALTER 全 try/catch (幂等) · CREATE TABLE IF NOT EXISTS
 *
 * 团队后续 W2-W3 补 13 *Home 需要的额外字段:
 *   009: customers status 中文标准化 (替代 'deleted' = '已删除' 等)
 *   010: products / work_orders schema 对齐 13 home renderer
 */

import type { Database } from 'sql.js';

export type Migration = (db: Database) => void;

export const migrations: Record<string, Migration> = {
  '001_material_is_active': (db) => {
    // 复用 v200.16.1 ALTER 模式 · 容错 (表可能 seed 后才存在)
    try {
      db.exec(`ALTER TABLE raw_materials ADD COLUMN is_active INTEGER DEFAULT 1`);
    } catch {
      /* duplicate column 或 表不存在 · 都跳过 */
    }
    try {
      db.exec(`ALTER TABLE auxiliary_materials ADD COLUMN is_active INTEGER DEFAULT 1`);
    } catch {
      /* same */
    }
    try {
      db.exec(`UPDATE raw_materials SET is_active = 1 WHERE is_active IS NULL`);
    } catch {
      /* 表不存在跳过 */
    }
    try {
      db.exec(`UPDATE auxiliary_materials SET is_active = 1 WHERE is_active IS NULL`);
    } catch {
      /* 表不存在跳过 */
    }
  },

  '002_handoff_workflow': (db) => {
    db.exec(`
      CREATE TABLE IF NOT EXISTS handoff_events (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        event_code TEXT NOT NULL,
        ref_type TEXT,
        ref_id INTEGER,
        actor_id INTEGER,
        recipient_roles TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_handoff_event_code ON handoff_events(event_code);
      CREATE INDEX IF NOT EXISTS idx_handoff_created ON handoff_events(created_at);
    `);
  },

  '003_customer_invoices_vat': (db) => {
    try {
      db.exec(`ALTER TABLE customer_invoices ADD COLUMN vat_rate REAL DEFAULT 0.08`);
    } catch {
      /* duplicate */
    }
    try {
      db.exec(`ALTER TABLE customer_invoices ADD COLUMN tax_amount REAL`);
    } catch {
      /* duplicate */
    }
    try {
      db.exec(`ALTER TABLE customer_invoices ADD COLUMN amount_before_tax REAL`);
    } catch {
      /* duplicate */
    }
  },

  '004_audit_events': (db) => {
    db.exec(`
      CREATE TABLE IF NOT EXISTS audit_events (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        actor_id INTEGER,
        actor_role TEXT,
        module TEXT,
        action TEXT,
        severity TEXT,
        title TEXT,
        body TEXT,
        ref_type TEXT,
        ref_id INTEGER,
        ip TEXT,
        user_agent TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_audit_actor ON audit_events(actor_id);
      CREATE INDEX IF NOT EXISTS idx_audit_module ON audit_events(module);
      CREATE INDEX IF NOT EXISTS idx_audit_created ON audit_events(created_at);
    `);
  },

  // v18.9.316 · quotations schema 5 新列 (seed.ts 创建新表已含, 老 db 走此 ALTER)
  '009_quotations_full_fields': (db) => {
    const cols = ['product_description TEXT', 'qty INTEGER', 'unit_price REAL', 'valid_until TEXT', 'notes TEXT'];
    for (const col of cols) {
      try {
        db.exec(`ALTER TABLE quotations ADD COLUMN ${col}`);
      } catch {
        /* duplicate column 跳过 · 表不存在跳过 */
      }
    }
  },

  // v18.9.318 Sprint 8-A · 核心 P0 补全 · notifications + complaints + returns
  '010_notifications_complaints': (db) => {
    db.exec(`
      CREATE TABLE IF NOT EXISTS notifications (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        recipient_role TEXT,
        recipient_code TEXT,
        title TEXT NOT NULL,
        body TEXT,
        severity TEXT DEFAULT 'info',
        ref_type TEXT,
        ref_id INTEGER,
        ref_hash TEXT,
        read_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_notif_role ON notifications(recipient_role);
      CREATE INDEX IF NOT EXISTS idx_notif_code ON notifications(recipient_code);
      CREATE INDEX IF NOT EXISTS idx_notif_read ON notifications(read_at);
      CREATE INDEX IF NOT EXISTS idx_notif_created ON notifications(created_at);

      CREATE TABLE IF NOT EXISTS complaints (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        customer_id INTEGER,
        so_id INTEGER,
        complaint_type TEXT,
        severity TEXT DEFAULT 'medium',
        title TEXT,
        description TEXT,
        opened_by INTEGER,
        opened_at TEXT DEFAULT CURRENT_TIMESTAMP,
        assigned_to INTEGER,
        resolved_at TEXT,
        resolution TEXT,
        status TEXT DEFAULT 'open',
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_complaints_status ON complaints(status);
      CREATE INDEX IF NOT EXISTS idx_complaints_customer ON complaints(customer_id);

      CREATE TABLE IF NOT EXISTS returns (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        customer_id INTEGER,
        so_id INTEGER,
        return_type TEXT,
        qty INTEGER,
        amount REAL,
        reason TEXT,
        status TEXT DEFAULT 'pending',
        compensation TEXT,
        opened_at TEXT DEFAULT CURRENT_TIMESTAMP,
        closed_at TEXT,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_returns_status ON returns(status);

      CREATE TABLE IF NOT EXISTS dunning_logs (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        invoice_id INTEGER,
        customer_id INTEGER,
        method TEXT,
        sent_at TEXT DEFAULT CURRENT_TIMESTAMP,
        sent_by INTEGER,
        notes TEXT
      );
    `);
  },

  // v18.9.352.2 hotfix · 强制再跑 (老 DB 已 applied 017 但缺数据 · 用新 ID 018 强制 re-run)
  '018_real_equipment_v352_2': (db) => {
    // ─── 1. equipment 表 · 直接 CREATE 完整 17 列 (fresh) + ALTER 加列 (existing) ───
    db.exec(`
      CREATE TABLE IF NOT EXISTS equipment (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        name TEXT,
        status TEXT DEFAULT 'running',
        is_active INTEGER DEFAULT 1,
        department TEXT,
        model TEXT,
        specification TEXT,
        qty INTEGER DEFAULT 1,
        equipment_type TEXT,
        next_maint_date TEXT,
        next_maint_hours REAL,
        next_maint_qty INTEGER,
        last_maint_date TEXT,
        last_maint_qty INTEGER,
        pm_cycle_days INTEGER,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
    `);
    // existing DB ALTER (idempotent · 已存在跳过 = 升级老 DB schema)
    const eqCols = ['department TEXT', 'model TEXT', 'specification TEXT', 'qty INTEGER DEFAULT 1', 'equipment_type TEXT', 'next_maint_date TEXT', 'next_maint_hours REAL', 'next_maint_qty INTEGER', 'last_maint_date TEXT', 'last_maint_qty INTEGER', 'pm_cycle_days INTEGER', 'deleted_at TEXT'];
    for (const col of eqCols) {
      try { db.exec(`ALTER TABLE equipment ADD COLUMN ${col}`); } catch { /* 幂等 · 已存在 */ }
    }

    // ─── 2. processes 表 · 同样 CREATE 完整 + ALTER ───
    db.exec(`
      CREATE TABLE IF NOT EXISTS processes (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        name_cn TEXT,
        name_vi TEXT,
        equipment_type TEXT,
        std_minutes REAL,
        cost_per_min REAL,
        seq_no INTEGER,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
    `);
    const procCols = ['equipment_type TEXT', 'std_minutes REAL', 'cost_per_min REAL', 'seq_no INTEGER', 'deleted_at TEXT'];
    for (const col of procCols) {
      try { db.exec(`ALTER TABLE processes ADD COLUMN ${col}`); } catch { /* 幂等 */ }
    }

    try { db.exec(`CREATE INDEX IF NOT EXISTS idx_eq_dept ON equipment(department)`); } catch {}
    try { db.exec(`CREATE INDEX IF NOT EXISTS idx_eq_type ON equipment(equipment_type)`); } catch {}
    try { db.exec(`CREATE INDEX IF NOT EXISTS idx_proc_type ON processes(equipment_type)`); } catch {}

    // ─── 3. 清旧 fake 数据 (EQ-001 ~ EQ-034) · 防 v352 部署前 fake + v352 真的混 ───
    try { db.exec(`DELETE FROM equipment WHERE code LIKE 'EQ-%'`); } catch {}
    // 旧通用 PROC-01..PROC-16 删 (替换为真 12 工艺)
    try { db.exec(`DELETE FROM processes WHERE code LIKE 'PROC-%' AND COALESCE(equipment_type,'') = ''`); } catch {}

    // ─── 4. INSERT 34 真设备 · Trungnam 2026-04-09 ───
    const realEqs = [
      // 印刷机 Printing (8 台)
      ['F1-PSN7D1218',  '6C+1 全自动印刷机 (含模切/压痕)',  '印刷机', 'printing',  '1200×1800mm · 9000pcs/h',  1],
      ['F3-DF4S1628',   '4C 东方全自动印刷机',                '印刷机', 'printing',  '1600×2800mm · 10000pcs/h', 1],
      ['F4-DL4SD1224',  '4C 自动印刷机 (含模切/压痕)',       '印刷机', 'printing',  '1200×2400mm · 10800pcs/h', 1],
      ['F5-DS1D2229',   '1C 单色开槽模切印刷机',              '印刷机', 'printing',  '2200×2900mm · 1600pcs/h',  1],
      ['F6-HY2SD2232',  '2C 自动印刷机 (含模切/压痕)',       '印刷机', 'printing',  '2200×3200mm · 8000pcs/h',  1],
      ['KB2500-12',     '12 头数码印刷机',                    '印刷机', 'printing',  '2500mm · 1000m²/h',         1],
      ['KB2500-4',      '4 头数码印刷机',                     '印刷机', 'printing',  '2500mm · 300m²/h',          1],
      ['DIG1100-1',     'EPSON 爱普生打稿机',                 '印刷机', 'prototype', '1100mm',                    1],
      // 制版机 (1)
      ['LASER100',      '激光雕版机',                         '制版机', 'plate',     '1300×1000mm',               2],
      // 介样机 (1)
      ['CNC1625',       'CNC 自动介样机 (刀模)',              '介样机', 'prototype', '1600×2500mm',               1],
      // 模切机 (6)
      ['DIECUT1-1510',  '半自动模切机',                       '模切机', 'diecut',    '1500×1000mm · 3000pcs/h',   1],
      ['DIECUT2-2215',  '半自动模切机',                       '模切机', 'diecut',    '2200×1500mm · 3000pcs/h',   1],
      ['DIECUT3-1813',  '全自动模切机',                       '模切机', 'diecut',    '1800×1300mm · 1000pcs/h',   1],
      ['DIECUT4-2214',  '全自动模切机',                       '模切机', 'diecut',    '2200×1400mm · 800pcs/h',    1],
      ['DIECUT5-2516',  '全自动模切机',                       '模切机', 'diecut',    '2500×1600mm · 500pcs/h',    1],
      ['DIECUT6-1007',  '全自动模切机 (高速)',                '模切机', 'diecut',    '1020×720mm · 5000pcs/h',    1],
      // 打钉机 (4)
      ['DONGDINH1-2400', '伺服双联半自动打钉机',              '打钉机', 'nail',      '2400mm · 1000pcs/h',        1],
      ['DONGDINH2-2800', '伺服双联半自动打钉机',              '打钉机', 'nail',      '2800mm · 1000pcs/h',        1],
      ['DONGDINH3-3000', '伺服双联半自动打钉机',              '打钉机', 'nail',      '3000mm · 1000pcs/h',        1],
      ['DONGDINHS1-2400','伺服单片半自动打钉机',              '打钉机', 'nail',      '2400mm · 1000pcs/h',        1],
      // 粘合机 (2)
      ['DANHO1-2400',   '全自动粘合机',                       '粘合机', 'gluing',    '2400mm · 70m/min',          1],
      ['DANDONGDINH1-2400', '半自动粘合机',                   '粘合机', 'gluing',    '2800mm · 70m/min',          1],
      // 裱纸机 (5)
      ['DANTEMTU01-2222','全自动裱纸机',                      '裱纸机', 'lamination','2200×2200mm · 9000pcs/h',   1],
      ['MayXa3000',     '分纸机',                             '裱纸机', 'lamination','3000mm · 2000pcs/h',        1],
      ['MayXa2500',     '分纸机',                             '裱纸机', 'lamination','2500mm · 2000pcs/h',        1],
      ['Maychap3000',   '打角机',                             '裱纸机', 'lamination','3000mm · 2000pcs/h',        1],
      ['Maychap2500',   '打角机',                             '裱纸机', 'lamination','2500mm · 2000pcs/h',        1],
      // 贴标机 (qty=14)
      ['DANTEMAY01-1200','贴标机',                            '贴标机', 'label',     '1200mm · 3000pcs/h',        14],
      // 实验室 (6)
      ['BST1',          '耐破强度测试仪 (Bursting Test)',     '实验室', 'lab',       '-',                         1],
      ['ECT1',          '边压强度测试仪 (Edge Crush Test)',   '实验室', 'lab',       '-',                         1],
      ['RuberT1',       '油墨脱色测试仪 (Rub Test)',          '实验室', 'lab',       '-',                         1],
      ['X-Rite',        'X-Rite 分光密度仪',                  '实验室', 'lab',       '-',                         1],
      ['i1',            '爱色丽 i1 色彩扫描仪',               '实验室', 'lab',       '-',                         1],
      ['HeavyMetalDetector', '重金属检测仪',                  '实验室', 'lab',       '-',                         1],
    ];
    for (const [code, name, dept, eqType, spec, qty] of realEqs) {
      try {
        db.run(`INSERT OR IGNORE INTO equipment (code, name, department, equipment_type, specification, qty, status, is_active) VALUES (?, ?, ?, ?, ?, ?, 'running', 1)`,
          [code, name, dept, eqType, spec, qty]);
      } catch { /* 重复 INSERT 跳过 */ }
    }

    // ─── 5. INSERT 12 真工艺 ───
    const realProcs = [
      ['PROC-T01', '打稿',      'In bản nháp',        'prototype',   30,  1200, 1],
      ['PROC-T02', '制版',      'Làm bản in',         'plate',       60,  1500, 2],
      ['PROC-T03', '介样/刀模', 'Khuôn mẫu',          'prototype',   90,  1800, 3],
      ['PROC-T04', '裱纸',      'Dán giấy',           'lamination',  10,  2000, 4],
      ['PROC-T05', '印刷',      'In',                 'printing',    8,   3500, 5],
      ['PROC-T06', '模切',      'Bế (Die-cut)',       'diecut',      15,  2500, 6],
      ['PROC-T07', '粘合',      'Dán keo',            'gluing',      6,   1800, 7],
      ['PROC-T08', '钉箱',      'Đóng đinh',          'nail',        5,   1500, 8],
      ['PROC-T09', '分纸',      'Cắt giấy',           'lamination',  8,   1600, 9],
      ['PROC-T10', '打角',      'Cắt góc',            'lamination',  8,   1600, 10],
      ['PROC-T11', '贴标',      'Dán nhãn',           'label',       4,   1200, 11],
      ['PROC-T12', 'QC 检验',   'Kiểm tra QC',        'lab',         15,  2000, 12],
    ];
    for (const [code, cn, vi, eqType, stdMin, cost, seq] of realProcs) {
      try {
        db.run(`INSERT OR IGNORE INTO processes (code, name_cn, name_vi, equipment_type, std_minutes, cost_per_min, seq_no) VALUES (?, ?, ?, ?, ?, ?, ?)`,
          [code, cn, vi, eqType, stdMin, cost, seq]);
      } catch { /* 跳过 */ }
    }
  },

  // v18.9.350 Sprint 9-A · 三级厂核心 schema (算料 + 唛头 + PM 字段)
  '016_paper_calc_pm': (db) => {
    // 订单用料表 (对标金软 ERP 16 列)
    db.exec(`
      CREATE TABLE IF NOT EXISTS order_materials (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        sales_order_id INTEGER,
        product_id INTEGER,
        material_code TEXT,
        material_name TEXT,
        paper_width REAL,
        paper_length REAL,
        finished_width REAL,
        finished_length REAL,
        bleed REAL DEFAULT 3,
        order_qty INTEGER,
        width_cuts INTEGER,
        length_cuts INTEGER,
        per_sheet INTEGER,
        sheets_needed INTEGER,
        actual_produced INTEGER,
        waste_qty INTEGER,
        waste_pct REAL,
        utilization_pct REAL,
        paper_area_sqm REAL,
        orientation TEXT,
        layout_mode TEXT DEFAULT 'auto',
        price_per_sqm REAL,
        estimated_cost REAL,
        notes TEXT,
        created_by INTEGER,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        deleted_at TEXT
      );
      CREATE INDEX IF NOT EXISTS idx_ordmat_so ON order_materials(sales_order_id);
      CREATE INDEX IF NOT EXISTS idx_ordmat_prod ON order_materials(product_id);
    `);

    // 唛头管理 (出口订单 mark code · 物理刻字溯源)
    db.exec(`
      CREATE TABLE IF NOT EXISTS mark_codes (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        sales_order_id INTEGER,
        customer_id INTEGER,
        product_id INTEGER,
        mark_text TEXT,
        mark_qty INTEGER,
        export_country TEXT,
        export_port TEXT,
        notes TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        deleted_at TEXT
      );
      CREATE INDEX IF NOT EXISTS idx_mark_so ON mark_codes(sales_order_id);
      CREATE INDEX IF NOT EXISTS idx_mark_cust ON mark_codes(customer_id);
    `);

    // equipment 加 PM 字段 (运行小时累计 + 产量累计阈值)
    const eqAlterCols = [
      'next_maint_hours REAL',
      'next_maint_qty INTEGER',
      'last_maint_qty INTEGER',
      'pm_cycle_days INTEGER',
    ];
    for (const col of eqAlterCols) {
      try {
        db.exec(`ALTER TABLE equipment ADD COLUMN ${col}`);
      } catch { /* 幂等 · 已存在跳过 */ }
    }

    // maintenance_tasks 加 request_type 字段 (preventive / corrective)
    try {
      db.exec(`ALTER TABLE maintenance_tasks ADD COLUMN request_type TEXT DEFAULT 'corrective'`);
    } catch { /* 幂等 */ }
    try {
      db.exec(`ALTER TABLE maintenance_tasks ADD COLUMN description TEXT`);
    } catch { /* 幂等 */ }
    try {
      db.exec(`ALTER TABLE maintenance_tasks ADD COLUMN duration_minutes INTEGER`);
    } catch { /* 幂等 */ }
    try {
      db.exec(`ALTER TABLE maintenance_tasks ADD COLUMN labor_cost REAL`);
    } catch { /* 幂等 */ }
    try {
      db.exec(`ALTER TABLE maintenance_tasks ADD COLUMN spare_parts_cost REAL`);
    } catch { /* 幂等 */ }
  },

  // v18.9.346 P3 · contracts 表 (Order360 timeline 节点 2 合同关联)
  '015_contracts_table': (db) => {
    db.exec(`
      CREATE TABLE IF NOT EXISTS contracts (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        customer_id INTEGER,
        contract_type TEXT,
        total_amount REAL,
        signed_date TEXT,
        valid_until TEXT,
        status TEXT DEFAULT 'draft',
        notes TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_contract_cust ON contracts(customer_id);
    `);
  },

  // v18.9.340 P1 · 请假/加班/培训/工资条 schema
  '014_v340_hr_workflow': (db) => {
    db.exec(`
      CREATE TABLE IF NOT EXISTS leave_requests (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        employee_id INTEGER,
        leave_type TEXT,
        start_date TEXT,
        end_date TEXT,
        days REAL,
        reason TEXT,
        status TEXT DEFAULT 'pending',
        approved_by INTEGER,
        approved_at TEXT,
        rejection_reason TEXT,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_leave_status ON leave_requests(status);
      CREATE INDEX IF NOT EXISTS idx_leave_emp ON leave_requests(employee_id);

      CREATE TABLE IF NOT EXISTS overtime_requests (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        employee_id INTEGER,
        overtime_date TEXT,
        start_time TEXT,
        end_time TEXT,
        hours REAL,
        reason TEXT,
        status TEXT DEFAULT 'pending',
        approved_by INTEGER,
        approved_at TEXT,
        rejection_reason TEXT,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_ot_status ON overtime_requests(status);

      CREATE TABLE IF NOT EXISTS training_records (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        title TEXT,
        category TEXT,
        instructor TEXT,
        training_date TEXT,
        duration_hours REAL,
        attendee_ids TEXT,
        notes TEXT,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );

      CREATE TABLE IF NOT EXISTS payslips (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        employee_id INTEGER,
        period TEXT,
        base_salary REAL DEFAULT 0,
        piecework_wage REAL DEFAULT 0,
        overtime_pay REAL DEFAULT 0,
        bonus REAL DEFAULT 0,
        deductions REAL DEFAULT 0,
        social_insurance REAL DEFAULT 0,
        tax REAL DEFAULT 0,
        net_pay REAL DEFAULT 0,
        notes TEXT,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_payslip_emp_period ON payslips(employee_id, period);
    `);
  },

  // v18.9.335 Sprint 8-G/E/N/K · 财务+仓储+工人+保卫+生产 大批量 schema
  '013_v335_bigbang': (db) => {
    // 银行账户
    db.exec(`
      CREATE TABLE IF NOT EXISTS bank_accounts (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        bank_name TEXT,
        account_no TEXT,
        account_name TEXT,
        swift_code TEXT,
        currency TEXT DEFAULT 'VND',
        opening_balance REAL DEFAULT 0,
        is_active INTEGER DEFAULT 1,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE TABLE IF NOT EXISTS vouchers (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        voucher_date TEXT,
        voucher_type TEXT,
        amount REAL,
        debit_account TEXT,
        credit_account TEXT,
        memo TEXT,
        created_by INTEGER,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_voucher_date ON vouchers(voucher_date);
      CREATE TABLE IF NOT EXISTS expenses (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        employee_id INTEGER,
        amount REAL,
        category TEXT,
        description TEXT,
        receipt_date TEXT,
        status TEXT DEFAULT 'pending',
        hr_reviewed_by INTEGER,
        hr_reviewed_at TEXT,
        fin_reviewed_by INTEGER,
        fin_reviewed_at TEXT,
        rejection_reason TEXT,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_expense_status ON expenses(status);
      CREATE INDEX IF NOT EXISTS idx_expense_emp ON expenses(employee_id);
      CREATE TABLE IF NOT EXISTS worker_attendance (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        employee_id INTEGER,
        attendance_date TEXT,
        check_in_at TEXT,
        check_out_at TEXT,
        late_minutes INTEGER DEFAULT 0,
        overtime_minutes INTEGER DEFAULT 0,
        status TEXT DEFAULT 'present',
        notes TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_att_emp_date ON worker_attendance(employee_id, attendance_date);
      CREATE TABLE IF NOT EXISTS oee_records (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        equipment_id INTEGER,
        record_date TEXT,
        shift TEXT,
        availability REAL,
        performance REAL,
        quality REAL,
        oee REAL,
        notes TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_oee_date ON oee_records(record_date);
      CREATE INDEX IF NOT EXISTS idx_oee_equip ON oee_records(equipment_id);
    `);
    // work_orders 扩 (current_process_id + phase 日数 for woTracking)
    const woCols = [
      'current_process_id INTEGER',
      'first_article_status TEXT',
      'version INTEGER DEFAULT 1',
      'shipped_qty INTEGER DEFAULT 0',
    ];
    for (const col of woCols) {
      try { db.exec(`ALTER TABLE work_orders ADD COLUMN ${col}`); } catch { /* dup */ }
    }
    // guard_log 扩
    const glCols = [
      'plate_no TEXT', 'driver_name TEXT', 'driver_phone TEXT',
      'ref_code TEXT', 'photo_url TEXT', 'gps_lat REAL', 'gps_lng REAL',
      'qty_planned REAL', 'qty_actual REAL', 'qty_diff REAL',
      'notes TEXT', 'guard_id INTEGER', 'stamp_type TEXT',
    ];
    for (const col of glCols) {
      try { db.exec(`ALTER TABLE guard_log ADD COLUMN ${col}`); } catch { /* dup */ }
    }
    // guard_patrol_logs 扩
    const pgCols = [
      'guard_id INTEGER', 'gps_lat REAL', 'gps_lng REAL', 'status TEXT',
      'dwell_seconds INTEGER', 'sequence_no INTEGER',
    ];
    for (const col of pgCols) {
      try { db.exec(`ALTER TABLE guard_patrol_logs ADD COLUMN ${col}`); } catch { /* dup */ }
    }
    // guard_patrol_points (8 巡逻点)
    db.exec(`
      CREATE TABLE IF NOT EXISTS guard_patrol_points (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        name TEXT,
        gps_lat REAL,
        gps_lng REAL,
        sequence_no INTEGER,
        dwell_required_sec INTEGER DEFAULT 30,
        is_active INTEGER DEFAULT 1,
        notes TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
    `);
  },

  // v18.9.329 Sprint 8-C · 工程部 4 新表 + bom_items 扩
  '012_engineering_tables': (db) => {
    // bom_items 扩 (unit/critical/notes)
    const bomCols = ['unit TEXT', 'is_critical INTEGER DEFAULT 0', 'notes TEXT'];
    for (const col of bomCols) {
      try { db.exec(`ALTER TABLE bom_items ADD COLUMN ${col}`); } catch { /* dup */ }
    }
    // kit_bom 套盒 BOM
    db.exec(`
      CREATE TABLE IF NOT EXISTS kit_bom (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        kit_product_id INTEGER,
        component_product_id INTEGER,
        qty REAL,
        notes TEXT,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_kit_bom_kit ON kit_bom(kit_product_id);

      CREATE TABLE IF NOT EXISTS alt_materials (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        primary_material_id INTEGER,
        alt_material_id INTEGER,
        priority INTEGER DEFAULT 1,
        notes TEXT,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_alt_mat ON alt_materials(primary_material_id);

      CREATE TABLE IF NOT EXISTS corrugated_recipes (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        name TEXT,
        flute_type TEXT,
        layers INTEGER,
        liner_top TEXT,
        liner_bottom TEXT,
        medium TEXT,
        gsm_total REAL,
        ect_target REAL,
        burst_target REAL,
        notes TEXT,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );

      CREATE TABLE IF NOT EXISTS artwork_reviews (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        product_id INTEGER,
        version TEXT,
        artwork_type TEXT,
        status TEXT DEFAULT 'pending',
        submitted_by INTEGER,
        submitted_at TEXT DEFAULT CURRENT_TIMESTAMP,
        reviewed_by INTEGER,
        reviewed_at TEXT,
        review_notes TEXT,
        file_url TEXT,
        deleted_at TEXT
      );
      CREATE INDEX IF NOT EXISTS idx_artwork_status ON artwork_reviews(status);

      CREATE TABLE IF NOT EXISTS cad_files (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        product_id INTEGER,
        version TEXT,
        file_type TEXT,
        file_url TEXT,
        size_kb INTEGER,
        uploaded_by INTEGER,
        uploaded_at TEXT DEFAULT CURRENT_TIMESTAMP,
        notes TEXT,
        deleted_at TEXT
      );
      CREATE INDEX IF NOT EXISTS idx_cad_product ON cad_files(product_id);
    `);
  },

  // v18.9.327 Sprint 8-J · maintenance 字段扩 + spare_parts 表
  '011_maintenance_schema': (db) => {
    // equipment 扩 (型号/位置/购置/MTBF/MTTR)
    const equipCols = [
      'model TEXT', 'location TEXT', 'purchase_date TEXT', 'manufacturer TEXT',
      'serial_no TEXT', 'last_maint_date TEXT', 'next_maint_date TEXT',
      'oee REAL', 'remark TEXT', 'deleted_at TEXT',
    ];
    for (const col of equipCols) {
      try { db.exec(`ALTER TABLE equipment ADD COLUMN ${col}`); } catch { /* dup */ }
    }
    // maintenance_tasks 扩 (类型/请求人/分派/优先级/完工/工时)
    const mtCols = [
      'code TEXT', 'task_type TEXT', 'priority TEXT DEFAULT "medium"',
      'description TEXT', 'requester_id INTEGER', 'assigned_to INTEGER',
      'started_at TEXT', 'completed_at TEXT', 'duration_minutes INTEGER',
      'cost REAL', 'parts_used TEXT', 'resolution TEXT',
      'deleted_at TEXT', 'closed_at TEXT',
    ];
    for (const col of mtCols) {
      try { db.exec(`ALTER TABLE maintenance_tasks ADD COLUMN ${col}`); } catch { /* dup */ }
    }
    // spare_parts 表
    db.exec(`
      CREATE TABLE IF NOT EXISTS spare_parts (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        code TEXT UNIQUE,
        name TEXT,
        category TEXT,
        unit TEXT,
        stock_qty REAL DEFAULT 0,
        min_qty REAL DEFAULT 0,
        unit_price REAL DEFAULT 0,
        location TEXT,
        supplier_id INTEGER,
        last_purchase_date TEXT,
        deleted_at TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      );
      CREATE INDEX IF NOT EXISTS idx_spare_code ON spare_parts(code);
      CREATE INDEX IF NOT EXISTS idx_spare_stock ON spare_parts(stock_qty);
    `);
  },
};

/**
 * 团队后续添加 migration 的 contract:
 *   - id 必须 NNN_description 格式 (按 ID 排序执行)
 *   - 必须幂等 (CREATE TABLE IF NOT EXISTS · ALTER try/catch)
 *   - 不依赖其他 migration 顺序 (除非显式 ID 排序)
 *   - 失败时不抛 (try/catch 内部) · runner.ts 会捕获记录
 */

// ===========================================================
// v365.1 hotfix · purchase_orders 加 paid_amount + invoice_date + due_date 等 AP 必备列
// 真测发现 APInvoiceList SQL 引用 po.paid_amount 报"no such column"
// ===========================================================
migrations['029_v365_1_po_ap_columns'] = (db) => {
  for (const col of [
    'paid_amount REAL DEFAULT 0',
    'invoice_date TEXT',
    'due_date TEXT',
    'payment_term TEXT',
    'delivery_date TEXT',
    'description TEXT',
    'specification TEXT',
    'qty REAL',
    'unit TEXT',
    'currency TEXT DEFAULT "VND"',
    'pr_code TEXT',
    'purchaser_code TEXT',
    'warehouse TEXT',
    'vat_rate REAL DEFAULT 10',
    'tax_amount REAL DEFAULT 0',
    'amount_before_tax REAL',
  ]) {
    try { db.exec(`ALTER TABLE purchase_orders ADD COLUMN ${col}`); } catch { /* dup */ }
  }
};

// ===========================================================
// v365 Sprint I-D · P0 #12 订单损耗 + 利润核算 (Order P&L)
//   • order_pnl 表 · 按订单累加成本 + 收入 + 毛利
//   • 数据源:
//     - material_cost ← SUM(material_issues 关联此 SO 工单 · qty × unit_price)
//     - labor_cost    ← SUM(work_reports duration_min × hourly_rate)
//     - overhead_cost ← 月度间接费按订单工时占比分摊
//   • 状态: open (实时累加) → closed (财务月结锁定)
// ===========================================================
migrations['028_v365_order_pnl'] = (db) => {
  db.exec(`
    CREATE TABLE IF NOT EXISTS order_pnl (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      sales_order_id INTEGER NOT NULL,
      so_code TEXT,
      customer_id INTEGER,
      product_id INTEGER,
      planned_qty INTEGER,
      completed_qty INTEGER,
      shortage_qty INTEGER DEFAULT 0,
      revenue REAL DEFAULT 0,
      material_cost REAL DEFAULT 0,
      labor_cost REAL DEFAULT 0,
      overhead_cost REAL DEFAULT 0,
      other_cost REAL DEFAULT 0,
      total_cost REAL DEFAULT 0,
      gross_profit REAL DEFAULT 0,
      gross_margin_pct REAL DEFAULT 0,
      waste_qty INTEGER DEFAULT 0,
      waste_cost REAL DEFAULT 0,
      waste_pct REAL DEFAULT 0,
      status TEXT DEFAULT 'open',
      recalculated_at TEXT,
      closed_at TEXT,
      closed_by TEXT,
      notes TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      updated_at TEXT,
      deleted_at TEXT
    );
    CREATE INDEX IF NOT EXISTS idx_pnl_so ON order_pnl(sales_order_id);
    CREATE INDEX IF NOT EXISTS idx_pnl_customer ON order_pnl(customer_id);
    CREATE INDEX IF NOT EXISTS idx_pnl_status ON order_pnl(status);
    CREATE INDEX IF NOT EXISTS idx_pnl_closed ON order_pnl(closed_at);
  `);
};

// ===========================================================
// v364 Sprint I-C · P0 #11 缺数 + 退货 + 补数 异常场景
//   • shortage_logs   缺数事件 (WO 完工时实际 < 计划)
//   • rework_orders   补数/返工工单 (parent_wo_id 关联)
//   • customer_returns 客户退货 (扩 returns 表 + 状态机)
// ===========================================================
migrations['027_v364_shortage_rework_returns'] = (db) => {
  db.exec(`
    CREATE TABLE IF NOT EXISTS shortage_logs (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      wo_id INTEGER NOT NULL,
      wo_code TEXT,
      so_id INTEGER,
      customer_id INTEGER,
      product_id INTEGER,
      planned_qty INTEGER NOT NULL,
      actual_qty INTEGER NOT NULL,
      shortage_qty INTEGER NOT NULL,
      shortage_pct REAL,
      reason_category TEXT,
      reason_detail TEXT,
      status TEXT DEFAULT 'open',
      decision TEXT,
      rework_wo_id INTEGER,
      reported_by TEXT,
      reported_at TEXT DEFAULT CURRENT_TIMESTAMP,
      resolved_by TEXT,
      resolved_at TEXT,
      notes TEXT,
      deleted_at TEXT
    );
    CREATE INDEX IF NOT EXISTS idx_shortage_wo ON shortage_logs(wo_id);
    CREATE INDEX IF NOT EXISTS idx_shortage_so ON shortage_logs(so_id);
    CREATE INDEX IF NOT EXISTS idx_shortage_status ON shortage_logs(status);

    CREATE TABLE IF NOT EXISTS rework_orders (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      parent_wo_id INTEGER,
      parent_wo_code TEXT,
      shortage_id INTEGER,
      return_id INTEGER,
      so_id INTEGER,
      customer_id INTEGER,
      product_id INTEGER,
      qty_to_remake INTEGER NOT NULL,
      reason_category TEXT,
      reason_detail TEXT,
      priority TEXT DEFAULT 'high',
      status TEXT DEFAULT 'planned',
      new_wo_id INTEGER,
      planned_completion_date TEXT,
      actual_completion_date TEXT,
      created_by TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      notes TEXT,
      deleted_at TEXT
    );
    CREATE INDEX IF NOT EXISTS idx_rework_parent ON rework_orders(parent_wo_id);
    CREATE INDEX IF NOT EXISTS idx_rework_shortage ON rework_orders(shortage_id);
    CREATE INDEX IF NOT EXISTS idx_rework_return ON rework_orders(return_id);
    CREATE INDEX IF NOT EXISTS idx_rework_status ON rework_orders(status);
  `);

  // 扩 returns 表 (老 returns 表已存在 · 加新列幂等 ALTER)
  for (const col of [
    'return_type TEXT DEFAULT "quality"',     // quality/shortage/customer_reject/wrong_spec
    'severity TEXT DEFAULT "medium"',          // critical/high/medium/low
    'decision TEXT',                           // rework/refund/credit_note/scrap/concession
    'rework_wo_id INTEGER',                    // 关联补数工单
    'refund_amount REAL DEFAULT 0',
    'credit_note_no TEXT',
    'photo_url TEXT',                          // 客户提供的不良品照片
    'received_at TEXT',
    'processed_by TEXT',
    'processed_at TEXT',
  ]) {
    try { db.exec(`ALTER TABLE returns ADD COLUMN ${col}`); } catch { /* dup */ }
  }
};

// ===========================================================
// v363 Sprint J-3 · 合同模板系统 (4 表 + 15 SEED 段落)
//   • contracts        合同主表
//   • contract_templates 合同模板 (按类型分: sales/purchase/distribution/oem)
//   • contract_sections   模板段落映射 (template × section_no)
//   • contract_clauses    标准条款库 (15 SEED · ZH+VI 双语)
// ===========================================================
migrations['026_v363_contract_templates'] = (db) => {
  // 老 015_contracts_table 已建 contracts 最小骨架 (code/customer_id/contract_type/total_amount/signed_date/valid_until/status/notes/created_at)
  // 此 migration 不重建 · 用 ALTER ADD COLUMN 幂等扩展 (兼容生产 IndexedDB 已存 schema)
  db.exec(`
    CREATE TABLE IF NOT EXISTS contracts (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      customer_id INTEGER,
      contract_type TEXT,
      total_amount REAL,
      status TEXT DEFAULT 'draft',
      notes TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
  `);
  // v363 加扩列 (idempotent · 已存在则 try/catch 跳过)
  for (const col of [
    'title TEXT',
    'template_id INTEGER',
    'supplier_id INTEGER',
    'sales_order_id INTEGER',
    'currency TEXT DEFAULT "VND"',
    'payment_term TEXT',
    'sign_date TEXT',
    'effective_date TEXT',
    'expire_date TEXT',
    'our_signer TEXT',
    'counterparty_signer TEXT',
    'version INTEGER DEFAULT 1',
    'parent_contract_id INTEGER',
    'placeholder_values TEXT',
    'deleted_at TEXT',
    'created_by TEXT',
    'updated_at TEXT',
  ]) {
    try { db.exec(`ALTER TABLE contracts ADD COLUMN ${col}`); } catch { /* dup */ }
  }
  // 索引 · 列存在后再建
  try { db.exec(`CREATE INDEX IF NOT EXISTS idx_contract_code ON contracts(code)`); } catch { /* */ }
  try { db.exec(`CREATE INDEX IF NOT EXISTS idx_contract_customer ON contracts(customer_id)`); } catch { /* */ }
  try { db.exec(`CREATE INDEX IF NOT EXISTS idx_contract_status ON contracts(status)`); } catch { /* */ }
  try { db.exec(`CREATE INDEX IF NOT EXISTS idx_contract_template ON contracts(template_id)`); } catch { /* */ }

  db.exec(`
    CREATE TABLE IF NOT EXISTS contract_templates (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      title_zh TEXT,
      title_vi TEXT,
      contract_type TEXT,
      lang TEXT DEFAULT 'zh-vi',
      recommended INTEGER DEFAULT 0,
      use_count INTEGER DEFAULT 0,
      parent_template_id INTEGER,
      version INTEGER DEFAULT 1,
      notes TEXT,
      deleted_at TEXT,
      created_by TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE INDEX IF NOT EXISTS idx_template_type ON contract_templates(contract_type);

    CREATE TABLE IF NOT EXISTS contract_sections (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      template_id INTEGER NOT NULL,
      section_no INTEGER NOT NULL,
      clause_id INTEGER,
      override_title_zh TEXT,
      override_title_vi TEXT,
      override_body_zh TEXT,
      override_body_vi TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE INDEX IF NOT EXISTS idx_section_template ON contract_sections(template_id, section_no);

    CREATE TABLE IF NOT EXISTS contract_clauses (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      category TEXT,
      title_zh TEXT,
      title_vi TEXT,
      body_zh TEXT,
      body_vi TEXT,
      placeholders TEXT,
      is_critical INTEGER DEFAULT 0,
      is_default INTEGER DEFAULT 1,
      use_count INTEGER DEFAULT 0,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE INDEX IF NOT EXISTS idx_clause_category ON contract_clauses(category);
  `);

  // 15 标准段落 SEED (ZH + VI 双语 · 越南纸箱业务通用)
  const clauses: Array<[string, string, string, string, string, string, string, number]> = [
    ['SPEC',     'product',   '第一条 · 产品规格',         'Điều 1 · Quy cách sản phẩm',     '甲乙双方约定甲方向乙方采购{{product_name}}, 产品规格如下: 箱型 {{box_type}} · 尺寸 {{dim_mm}} · 材料 {{material}} · 印刷 CMYK + PANTONE {{pantone}}. 所有规格以双方签字盖章的样板/技术规格书为准.', 'Hai bên thỏa thuận Bên A mua từ Bên B sản phẩm {{product_name}} với quy cách: Loại hộp {{box_type}} · Kích thước {{dim_mm}} · Vật tư {{material}} · In CMYK + PANTONE {{pantone}}. Tất cả quy cách theo mẫu/quy cách kỹ thuật đã ký đóng dấu.', 'product_name,box_type,dim_mm,material,pantone', 1],
    ['QTY',      'product',   '第二条 · 数量与交货',       'Điều 2 · Số lượng và giao hàng',  '总数量 {{total_qty}} 件 · 单价 {{unit_price}} VND/件 · 总金额 {{total_amount}} VND. 交货日期: {{delivery_date}}. 一次性交付/分批交付按附件 A 执行. 交货地点: {{delivery_address}}.', 'Tổng số lượng {{total_qty}} cái · Đơn giá {{unit_price}} VND/cái · Tổng giá trị {{total_amount}} VND. Ngày giao: {{delivery_date}}. Giao 1 lần/nhiều lần theo phụ lục A. Địa điểm: {{delivery_address}}.', 'total_qty,unit_price,total_amount,delivery_date,delivery_address', 1],
    ['PRICE',    'commercial', '第三条 · 价格与汇率',       'Điều 3 · Đơn giá và tỷ giá',      '本合同价格为 {{currency}} 含 10% VAT 不含运费. 如原料 (CCNB/纸板) 市场价波动 ±10% 以上, 双方可协商调整本合同价格. 汇率按签订日 BIDV 公布的中间价为准.', 'Giá hợp đồng tính bằng {{currency}} đã bao gồm 10% VAT, chưa bao gồm vận chuyển. Nếu giá NVL (CCNB/giấy carton) biến động ±10% trở lên, hai bên có thể thương lượng điều chỉnh. Tỷ giá theo BIDV ngày ký.', 'currency', 1],
    ['PAY',      'commercial', '第四条 · 付款方式',         'Điều 4 · Hình thức thanh toán',  '付款条件: {{payment_term}}. 银行账户: BIDV 美福分行 · VND 6521295397 · USD 6520012144 · SWIFT BIDVVNVX. 客户付款备注请注明本合同号 {{contract_code}}. 逾期付款按月息 1% 计利息.', 'Điều kiện TT: {{payment_term}}. Tài khoản: BIDV CN Mỹ Phước · VND 6521295397 · USD 6520012144 · SWIFT BIDVVNVX. Khi chuyển khoản KH vui lòng ghi mã HĐ {{contract_code}}. Quá hạn TT chịu lãi 1%/tháng.', 'payment_term,contract_code', 1],
    ['QUALITY',  'quality',   '第五条 · 质量标准',         'Điều 5 · Tiêu chuẩn chất lượng', '产品质量符合 GB/T 6543-2008 + 客户技术规范 (附件 B). 关键指标: ECT 边压 ≥ 4.5 N/cm · 破裂强度 ≥ 700 kPa · 颜色 ΔE ≤ 3.0 · 尺寸公差 ±2mm. 卖方在每批出厂前进行 OQC 抽检 (AQL II/2.5) 并出检验报告随货送达.', 'Chất lượng theo GB/T 6543-2008 + quy cách KH (Phụ lục B). Chỉ tiêu chính: ECT ≥ 4.5 N/cm · Sức xé ≥ 700 kPa · ΔE màu ≤ 3.0 · Dung sai ±2mm. Bên bán làm OQC (AQL II/2.5) trước xuất xưởng + báo cáo đi kèm hàng.', '', 1],
    ['ACCEPT',   'quality',   '第六条 · 验收与索赔',       'Điều 6 · Nghiệm thu và khiếu nại','买方收到货物后 7 个工作日内验收 · 如发现质量问题需立即拍照保留证据 + 邮件/Zalo 通知卖方 + 提供 OQC 报告 + 不良样品. 双方协商后 30 天内处理: 重做/换货/退款/折让. 超期未通知视为验收合格.', 'Bên mua nghiệm thu trong 7 ngày làm việc · Nếu có lỗi chất lượng phải chụp ảnh + thông báo qua email/Zalo + cung cấp báo cáo OQC + mẫu lỗi. Hai bên xử lý trong 30 ngày: làm lại/đổi hàng/hoàn tiền/giảm giá. Quá hạn không TB coi như đạt.', '', 1],
    ['PACK',     'logistics', '第七条 · 包装与标识',       'Điều 7 · Bao bì và nhãn mác',    '产品按客户提供的唛头/标签贴标 · 外纸箱印刷出货标识 (产品名/客户/数量/批号/到货 QR 码). 木质托盘 ISPM-15 国际运输标准. 装箱单 (Packing List) 一式三联 (卖方/买方/海关) 随货.', 'Bao bì theo nhãn KH cung cấp · Thùng ngoài in nhãn xuất hàng (Tên SP/KH/Số lượng/Lô/QR mã). Pallet gỗ ISPM-15. Packing List 3 liên (bán/mua/hải quan) đi kèm.', '', 0],
    ['SHIP',     'logistics', '第八条 · 运输与风险',       'Điều 8 · Vận chuyển và rủi ro',  'Incoterms 2020 · {{trade_term}}. 卖方负责发货前到 {{loading_port}} 的运输 · 买方负责后续. 货物风险从交付车辆 (FCA/EXW) 或船舷 (FOB) 起转移. 卖方为货物投保 110% 货值的运输险.', 'Incoterms 2020 · {{trade_term}}. Bên bán chịu vận chuyển đến {{loading_port}} · Bên mua chịu phần còn lại. Rủi ro chuyển khi giao xe (FCA/EXW) hoặc lan can tàu (FOB). Bên bán mua bảo hiểm 110% giá trị hàng.', 'trade_term,loading_port', 0],
    ['BREACH',   'legal',     '第九条 · 违约责任',         'Điều 9 · Trách nhiệm vi phạm',   '任一方违约应赔偿对方因此造成的实际损失. 逾期交货按每延误一天总金额 0.1% 支付违约金 (上限 10%). 逾期付款按月息 1% 计利息. 严重违约 (3 次以上质量不合格/连续 60 天未付款) 守约方可单方解除合同 + 索赔.', 'Bên vi phạm phải bồi thường tổn thất thực tế. Giao chậm phạt 0.1%/ngày (tối đa 10%). TT chậm chịu lãi 1%/tháng. Vi phạm nghiêm trọng (>3 lần lỗi CL/60 ngày chưa TT) bên kia có quyền đơn phương chấm dứt + đòi bồi thường.', '', 1],
    ['FORCE',    'legal',     '第十条 · 不可抗力',         'Điều 10 · Sự kiện bất khả kháng','因不可抗力 (战争/疫情/天灾/政府禁令/电力中断 7 天以上) 导致一方不能履行合同, 应及时书面通知对方 + 提供主管部门证明 · 双方协商延期/解除/重新议定. 不可抗力期间不计算违约金/利息.', 'Trường hợp bất khả kháng (chiến tranh/đại dịch/thiên tai/lệnh CP/cúp điện >7 ngày) một bên không thực hiện được HĐ, phải TB bằng văn bản + giấy CN của cơ quan thẩm quyền. Hai bên thương lượng gia hạn/chấm dứt/đàm phán lại. Trong thời gian này không tính phạt/lãi.', '', 0],
    ['DISPUTE',  'legal',     '第十一条 · 争议解决',       'Điều 11 · Giải quyết tranh chấp','双方因履行本合同发生争议, 应首先协商解决. 协商不成的, 任一方可向胡志明市国际仲裁中心 (VIAC) 提请仲裁. 仲裁裁决为终局, 对双方均有约束力. 仲裁费用由败诉方承担. 适用越南法律.', 'Hai bên có tranh chấp về HĐ thì thương lượng trước. Nếu không được, mỗi bên có quyền đưa ra Trung tâm Trọng tài Quốc tế Việt Nam (VIAC) tại TP.HCM. Phán quyết VIAC là cuối cùng, ràng buộc 2 bên. Phí trọng tài do bên thua chịu. Áp dụng luật Việt Nam.', '', 1],
    ['CONFID',   'legal',     '第十二条 · 保密',           'Điều 12 · Bảo mật',              '双方对本合同及履行过程中获悉的对方商业秘密 (客户名单/技术规范/价格/工艺/订单数据等) 负有保密义务, 保密期为合同终止后 5 年. 违反保密义务一方应赔偿对方实际损失.', 'Hai bên có nghĩa vụ bảo mật thông tin BMTM của bên kia (danh sách KH/quy cách KT/giá/công nghệ/dữ liệu đơn hàng) trong 5 năm sau khi HĐ kết thúc. Vi phạm phải bồi thường tổn thất thực tế.', '', 0],
    ['IP',       'legal',     '第十三条 · 知识产权',       'Điều 13 · Sở hữu trí tuệ',       '买方提供的图稿/印版/刀模/品牌 LOGO 等知识产权归买方所有, 卖方仅在本合同执行期间使用, 不得擅自使用于其他客户. 合同终止后卖方应在 30 天内归还/销毁相关图稿+印版+刀模并签字确认.', 'Bản vẽ/khuôn in/khuôn cắt/logo nhãn hiệu do bên mua cung cấp thuộc SHTT bên mua, bên bán chỉ dùng trong HĐ này, không dùng cho KH khác. Sau khi HĐ kết thúc bên bán hoàn trả/hủy trong 30 ngày + ký xác nhận.', '', 0],
    ['EFFECT',   'legal',     '第十四条 · 合同生效',       'Điều 14 · Hiệu lực hợp đồng',    '本合同自双方法定代表人签字 + 盖公章之日起生效, 有效期 {{validity_period}}. 合同正本一式 {{copies}} 份, 双方各执 {{each_party_copies}} 份, 具同等法律效力. 未尽事宜以双方书面补充协议为准.', 'HĐ này có hiệu lực từ ngày đại diện pháp luật 2 bên ký + đóng dấu, hiệu lực {{validity_period}}. HĐ chính {{copies}} bản, mỗi bên giữ {{each_party_copies}} bản, có giá trị pháp lý như nhau. Vấn đề chưa nêu sẽ có phụ lục bổ sung.', 'validity_period,copies,each_party_copies', 0],
    ['APPENDIX', 'legal',     '第十五条 · 附则',           'Điều 15 · Phụ lục',              '附件 A: 产品规格书 + 样板 / Phụ lục A: Quy cách SP + mẫu.\n附件 B: 质量技术规范 / Phụ lục B: Quy cách KT chất lượng.\n附件 C: 交货排期 / Phụ lục C: Lịch giao hàng.\n附件 D: 价格汇总表 / Phụ lục D: Bảng giá tổng hợp.\n所有附件作为本合同不可分割的一部分.', '', '', 0],
  ];
  for (const [code, cat, tZh, tVi, bZh, bVi, ph, crit] of clauses) {
    try {
      db.run(`INSERT OR IGNORE INTO contract_clauses (code, category, title_zh, title_vi, body_zh, body_vi, placeholders, is_critical) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [code, cat, tZh, tVi, bZh, bVi, ph, crit]);
    } catch { /* dup */ }
  }

  // 2 默认模板 (销售合同 + 采购合同)
  try {
    db.run(`INSERT OR IGNORE INTO contract_templates (code, title_zh, title_vi, contract_type, recommended) VALUES (?, ?, ?, ?, ?)`,
      ['SALES-STD', '销售合同 (标准版)', 'Hợp đồng bán hàng (chuẩn)', 'sales', 1]);
    db.run(`INSERT OR IGNORE INTO contract_templates (code, title_zh, title_vi, contract_type, recommended) VALUES (?, ?, ?, ?, ?)`,
      ['PURCHASE-STD', '采购合同 (标准版)', 'Hợp đồng mua hàng (chuẩn)', 'purchase', 1]);
  } catch { /* dup */ }

  // 映射 SALES-STD 模板 = 全 15 段落
  try {
    const salesTpl = db.exec(`SELECT id FROM contract_templates WHERE code='SALES-STD'`)[0]?.values?.[0]?.[0];
    if (salesTpl) {
      const clauseIds = db.exec(`SELECT id FROM contract_clauses ORDER BY id`)[0]?.values || [];
      for (let i = 0; i < clauseIds.length; i++) {
        const clauseId = clauseIds[i][0];
        try {
          db.run(`INSERT OR IGNORE INTO contract_sections (template_id, section_no, clause_id) VALUES (?, ?, ?)`,
            [salesTpl, i + 1, clauseId]);
        } catch { /* dup */ }
      }
    }
    const purchaseTpl = db.exec(`SELECT id FROM contract_templates WHERE code='PURCHASE-STD'`)[0]?.values?.[0]?.[0];
    if (purchaseTpl) {
      const clauseIds = db.exec(`SELECT id FROM contract_clauses ORDER BY id`)[0]?.values || [];
      for (let i = 0; i < clauseIds.length; i++) {
        const clauseId = clauseIds[i][0];
        try {
          db.run(`INSERT OR IGNORE INTO contract_sections (template_id, section_no, clause_id) VALUES (?, ?, ?)`,
            [purchaseTpl, i + 1, clauseId]);
        } catch { /* dup */ }
      }
    }
  } catch { /* schema not ready */ }
};

// ===========================================================
// v360.1 hotfix · 收料 QR 加 WO/SO/客户/采购员关联 (Jack 反馈 · 小票内容齐全)
// ===========================================================
migrations['025_v360_1_receipt_qr_relations'] = (db) => {
  for (const col of ['wo_id INTEGER', 'wo_code TEXT', 'so_id INTEGER', 'so_code TEXT', 'customer_name TEXT', 'purchaser_code TEXT', 'arrival_doc_no TEXT', 'gross_weight_kg REAL', 'package_count INTEGER']) {
    try { db.exec(`ALTER TABLE material_receipts_qr ADD COLUMN ${col}`); } catch { /* dup */ }
  }
};

// ===========================================================
// v360 Sprint I-B · P0 #10 来料扫码 + QR 收货小票
// ===========================================================
migrations['024_v360_receipt_qr'] = (db) => {
  db.exec(`
    CREATE TABLE IF NOT EXISTS material_receipts_qr (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      receipt_no TEXT UNIQUE,
      qr_code TEXT,
      material_type TEXT,
      material_id INTEGER,
      material_name TEXT,
      batch_no TEXT,
      qty REAL,
      unit TEXT DEFAULT '件',
      unit_price REAL DEFAULT 0,
      supplier_id INTEGER,
      supplier_name TEXT,
      po_no TEXT,
      pr_no TEXT,
      warehouse_location TEXT,
      receiver_code TEXT,
      receive_time TEXT,
      qc_status TEXT DEFAULT 'pending',
      qc_inspector TEXT,
      qc_time TEXT,
      printed_at TEXT,
      reprint_count INTEGER DEFAULT 0,
      notes TEXT,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE INDEX IF NOT EXISTS idx_receipt_no ON material_receipts_qr(receipt_no);
    CREATE INDEX IF NOT EXISTS idx_receipt_qr_code ON material_receipts_qr(qr_code);
    CREATE INDEX IF NOT EXISTS idx_receipt_supplier ON material_receipts_qr(supplier_id);
    CREATE INDEX IF NOT EXISTS idx_receipt_batch ON material_receipts_qr(batch_no);
  `);
};

// ===========================================================
// v359 Sprint I-A · P0 #9 排单前 4 件齐备 (原料/印版/刀模/油墨)
// ===========================================================
migrations['023_v359_wo_readiness_4items'] = (db) => {
  // 1. 印版库 (CTP 印版 · 客户专属 · PANTONE/CMYK 关联)
  db.exec(`
    CREATE TABLE IF NOT EXISTS printing_plates (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      name TEXT,
      customer_id INTEGER,
      product_id INTEGER,
      color_spec TEXT,
      plate_count INTEGER DEFAULT 4,
      status TEXT DEFAULT 'in_stock',
      location TEXT,
      last_use_date TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE INDEX IF NOT EXISTS idx_plate_customer ON printing_plates(customer_id);
    CREATE INDEX IF NOT EXISTS idx_plate_product ON printing_plates(product_id);
  `);
  // 2. 刀模库 (0201/0203/0904 等标准刀型 · 客户/产品专属刀)
  db.exec(`
    CREATE TABLE IF NOT EXISTS dies (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      name TEXT,
      die_type TEXT,
      dim_mm TEXT,
      customer_id INTEGER,
      product_id INTEGER,
      status TEXT DEFAULT 'in_stock',
      wear_level INTEGER DEFAULT 0,
      max_runs INTEGER DEFAULT 10000,
      current_runs INTEGER DEFAULT 0,
      location TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE INDEX IF NOT EXISTS idx_die_customer ON dies(customer_id);
  `);
  // 3. 油墨库 (CMYK + PANTONE 专色 · 库存 kg)
  db.exec(`
    CREATE TABLE IF NOT EXISTS inks (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      name TEXT,
      color_type TEXT,
      pantone_code TEXT,
      stock_kg REAL DEFAULT 0,
      reorder_kg REAL DEFAULT 5,
      unit_price REAL DEFAULT 0,
      supplier_id INTEGER,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE INDEX IF NOT EXISTS idx_ink_pantone ON inks(pantone_code);
  `);
  // 4. WO 4 件齐备状态 (1 WO 1 row · 4 项 status: ready/waiting/missing)
  db.exec(`
    CREATE TABLE IF NOT EXISTS wo_readiness (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      wo_id INTEGER NOT NULL,
      raw_material_status TEXT DEFAULT 'waiting',
      plate_status TEXT DEFAULT 'waiting',
      die_status TEXT DEFAULT 'waiting',
      ink_status TEXT DEFAULT 'waiting',
      raw_material_ref TEXT,
      plate_ref TEXT,
      die_ref TEXT,
      ink_ref TEXT,
      ready_at TEXT,
      checked_by TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      updated_at TEXT
    );
    CREATE INDEX IF NOT EXISTS idx_readiness_wo ON wo_readiness(wo_id);
  `);
  // 5. SEED · 1812800 真案例 4 件 (印版 + 刀模 + 油墨)
  try {
    // 印版 (CMYK + PANTONE 4230 C 外箱 · 485C 内箱)
    db.run(`INSERT OR IGNORE INTO printing_plates (code, name, color_spec, plate_count, status, location) VALUES (?, ?, ?, ?, 'in_stock', ?)`,
      ['PLATE-1812800-OUTER', '1812800 外箱印版 (CMYK + PANTONE 4230 C)', 'CMYK + PANTONE 4230 C', 5, '架 A-3']);
    db.run(`INSERT OR IGNORE INTO printing_plates (code, name, color_spec, plate_count, status, location) VALUES (?, ?, ?, ?, 'in_stock', ?)`,
      ['PLATE-1812800-INNER', '1812800 侧箱印版 (PANTONE 485C)', 'PANTONE 485C', 1, '架 A-4']);
    // 刀模 0201 / 0203 / 0904
    db.run(`INSERT OR IGNORE INTO dies (code, name, die_type, dim_mm, status, current_runs, max_runs, location) VALUES (?, ?, ?, ?, 'in_stock', ?, ?, ?)`,
      ['DIE-0201-1800x565x810', '1812800 外箱刀模 0201', '0201', '1800x565x810', 0, 10000, '刀模区 B-1']);
    db.run(`INSERT OR IGNORE INTO dies (code, name, die_type, dim_mm, status, current_runs, max_runs, location) VALUES (?, ?, ?, ?, 'in_stock', ?, ?, ?)`,
      ['DIE-0203-480x165x140', '1812800 侧封箱刀模 0203', '0203', '480x165x140', 0, 10000, '刀模区 B-2']);
    db.run(`INSERT OR IGNORE INTO dies (code, name, die_type, dim_mm, status, current_runs, max_runs, location) VALUES (?, ?, ?, ?, 'in_stock', ?, ?, ?)`,
      ['DIE-0904-1725x495', '1812800 纸板裁切刀 0904', '0904', '1725x495x3', 0, 10000, '刀模区 B-3']);
    // 油墨 CMYK 4 色 + PANTONE 4230 C + 485C
    const inks = [
      ['INK-C', '青色 Cyan', 'process', '', 25, 5, 280000],
      ['INK-M', '品红 Magenta', 'process', '', 22, 5, 280000],
      ['INK-Y', '黄色 Yellow', 'process', '', 30, 5, 260000],
      ['INK-K', '黑色 Black', 'process', '', 35, 5, 240000],
      ['INK-P4230C', 'PANTONE 4230 C (棕褐)', 'spot', '4230 C', 8, 2, 480000],
      ['INK-P485C', 'PANTONE 485 C (红)', 'spot', '485 C', 12, 2, 420000],
    ];
    for (const [code, name, type, pantone, kg, reorder, price] of inks) {
      db.run(`INSERT OR IGNORE INTO inks (code, name, color_type, pantone_code, stock_kg, reorder_kg, unit_price) VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [code, name, type, pantone, kg, reorder, price]);
    }
  } catch { /* dup ok */ }
};

// ===========================================================
// v358.5 hotfix · 全表 updated_at 补齐 (1812800 真测 P0 #14 真治)
// ===========================================================
migrations['022_v358_5_universal_updated_at'] = (db) => {
  for (const tbl of ['work_orders', 'work_reports', 'sales_orders', 'quotations', 'customer_payments', 'qc_records', 'inventory', 'products', 'customers', 'suppliers', 'raw_materials', 'purchase_orders', 'sales_order_lines', 'quotation_lines']) {
    try { db.exec(`ALTER TABLE ${tbl} ADD COLUMN updated_at TEXT`); } catch { /* dup or missing */ }
  }
};

// ===========================================================
// v358.3 hotfix · 全表 deleted_at + notes 补齐 (1812800 真测 P0 #7 真治)
// ===========================================================
migrations['021_v358_3_universal_deleted_at_notes'] = (db) => {
  // work_orders 缺 deleted_at + notes (seed.ts 漏)
  for (const col of ['deleted_at TEXT', 'notes TEXT']) {
    try { db.exec(`ALTER TABLE work_orders ADD COLUMN ${col}`); } catch { /* dup */ }
  }
  // sales_orders 缺 notes
  try { db.exec(`ALTER TABLE sales_orders ADD COLUMN notes TEXT`); } catch { /* dup */ }
  // quotations 兜底 (已有但保证)
  try { db.exec(`ALTER TABLE quotations ADD COLUMN deleted_at TEXT`); } catch { /* dup */ }
  // customer_payments / qc_records / work_reports / inventory 兜底
  for (const tbl of ['customer_payments', 'qc_records', 'work_reports', 'inventory', 'bom_items', 'products', 'employees', 'customers', 'suppliers', 'raw_materials', 'auxiliary_materials', 'purchase_orders', 'purchase_requisitions']) {
    try { db.exec(`ALTER TABLE ${tbl} ADD COLUMN deleted_at TEXT`); } catch { /* dup */ }
    try { db.exec(`ALTER TABLE ${tbl} ADD COLUMN notes TEXT`); } catch { /* dup */ }
  }
};

// ===========================================================
// v358.1 hotfix · 套盒多行子表 (1812800 真订单 desk review P0)
// ===========================================================
migrations['020_v358_1_lines_subtables'] = (db) => {
  // quotation_lines · 报价行项 (套盒 N 子组件)
  db.exec(`
    CREATE TABLE IF NOT EXISTS quotation_lines (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      quotation_id INTEGER NOT NULL,
      line_no INTEGER DEFAULT 1,
      product_id INTEGER,
      product_description TEXT,
      box_type TEXT,
      dim_mm TEXT,
      material TEXT,
      gsm_spec TEXT,
      area_m2 REAL,
      qty INTEGER NOT NULL,
      unit_price REAL NOT NULL,
      line_total REAL,
      notes TEXT,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE INDEX IF NOT EXISTS idx_qlines_quotation ON quotation_lines(quotation_id);
    CREATE INDEX IF NOT EXISTS idx_qlines_product ON quotation_lines(product_id);
  `);

  // sales_order_lines · SO 行项 (套盒 N 子组件)
  db.exec(`
    CREATE TABLE IF NOT EXISTS sales_order_lines (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      sales_order_id INTEGER NOT NULL,
      line_no INTEGER DEFAULT 1,
      product_id INTEGER,
      product_description TEXT,
      box_type TEXT,
      dim_mm TEXT,
      material TEXT,
      gsm_spec TEXT,
      area_m2 REAL,
      qty INTEGER NOT NULL,
      unit_price REAL NOT NULL,
      line_total REAL,
      wo_id INTEGER,
      notes TEXT,
      deleted_at TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE INDEX IF NOT EXISTS idx_solines_so ON sales_order_lines(sales_order_id);
    CREATE INDEX IF NOT EXISTS idx_solines_product ON sales_order_lines(product_id);
    CREATE INDEX IF NOT EXISTS idx_solines_wo ON sales_order_lines(wo_id);
  `);

  // quotations / sales_orders 兼容老数据扩列 (主行也存数据当作 line_no=1)
  for (const col of ['product_id INTEGER', 'box_type TEXT', 'dim_mm TEXT', 'material TEXT', 'qty INTEGER', 'unit_price REAL']) {
    try { db.exec(`ALTER TABLE quotations ADD COLUMN ${col}`); } catch { /* dup */ }
    try { db.exec(`ALTER TABLE sales_orders ADD COLUMN ${col}`); } catch { /* dup */ }
  }
};

// ===========================================================
// v358 · 业务/排单/报工 三大基石闭环 schema 扩展
// ===========================================================
migrations['019_v358_focus_business_pmc_report'] = (db) => {
  // 1. quotations / sales_orders / work_orders 加 urgency 列 (🔥 加急)
  for (const tbl of ['quotations', 'sales_orders', 'work_orders']) {
    try { db.exec(`ALTER TABLE ${tbl} ADD COLUMN urgency TEXT DEFAULT 'normal'`); } catch { /* dup */ }
  }
  // 2. work_orders 加 waiting_material 状态支持 (无需 schema 改, 只是约束放宽 · status 已是 TEXT)
  //    + 业务字段 actual_start_time / actual_end_time
  for (const col of ['actual_start_time TEXT', 'actual_end_time TEXT']) {
    try { db.exec(`ALTER TABLE work_orders ADD COLUMN ${col}`); } catch { /* dup */ }
  }
  // 3. work_reports 加 sessions JSON + duration_min + qty_ng + notes (多 session 工时累计)
  for (const col of [
    'sessions TEXT',
    'duration_min INTEGER DEFAULT 0',
    'qty_ng INTEGER DEFAULT 0',
    'notes TEXT',
    'status TEXT DEFAULT \'submitted\'',
  ]) {
    try { db.exec(`ALTER TABLE work_reports ADD COLUMN ${col}`); } catch { /* dup */ }
  }
  // 4. 索引: urgency 排序加速 (Gantt 顶置)
  try { db.exec(`CREATE INDEX IF NOT EXISTS idx_so_urgency ON sales_orders(urgency)`); } catch { /* tbl missing */ }
  try { db.exec(`CREATE INDEX IF NOT EXISTS idx_wo_urgency ON work_orders(urgency)`); } catch { /* same */ }
  try { db.exec(`CREATE INDEX IF NOT EXISTS idx_wr_worker_time ON work_reports(worker_code, report_time)`); } catch { /* same */ }
};
